import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useServices, types, isHttpExceptionModel } from "@/lib/openapi";
import { useToast } from "primevue/usetoast";

export type OrganizationUser = types.OrganizationUser;

export const useUsersStore = defineStore("users", () => {
    const toast = useToast();
    const users = ref<OrganizationUser[]>([]);
    const userRoles = ref<Record<string, types.DomainRole[]>>({});
    const isLoading = ref(false);
    const currentError = ref<string | null>(null);

    const api = useServices({
        opts: { throwOnError: false },
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    /**
     * Get all users for the current organization
     */
    const getAllUsers = async () => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } = await api.getUsersUserGet({});

            if (!response.ok) {
                currentError.value = "Failed to load users";
                return [];
            }

            users.value = data || [];
            return data;
        } catch (error) {
            handleError(error, "Failed to load users");
            return [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Get user domain roles for a specific user
     */
    const getUserRoles = async (userId: string) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } =
                await api.getDomainRolesUserUserIdDomainRolesGet({
                    path: {
                        user_id: userId,
                    },
                });

            if (!response.ok) {
                currentError.value = "Failed to load user roles";
                return [];
            }

            userRoles.value[userId] = data || [];
            return data;
        } catch (error) {
            handleError(error, "Failed to load user roles");
            return [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Add a user to the current organization
     */
    const addUserToOrganization = async (
        email: string,
        role: types.ClusterRole,
        name?: string
    ) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } = await api.createUserUserPost({
                body: {
                    email,
                    name: name || email.split("@")[0],
                },
            });

            if (!response.ok || !data) {
                currentError.value = "Failed to add user to organization";
                return false;
            }

            // If the role should be user-manager, update the role
            if (role === types.ClusterRole.USER_MANAGER) {
                await makeUserManager(data.identity);
            } else {
                // Refresh the user list after adding a new user
                await getAllUsers();
            }

            toast.add({
                severity: "success",
                summary: "User added successfully",
                life: 3000,
            });

            return true;
        } catch (error) {
            handleError(error, "Failed to add user to organization");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Remove a user from the current organization
     */
    const removeUserFromOrganization = async (userId: string) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response } = await api.deleteUserUserUserIdDelete({
                path: {
                    user_id: userId,
                },
            });

            if (!response.ok) {
                currentError.value = "Failed to remove user from organization";
                return false;
            }

            // Remove user from local state
            users.value = users.value.filter(
                (user) => user.identity !== userId
            );
            delete userRoles.value[userId];

            toast.add({
                severity: "success",
                summary: "User removed successfully",
                life: 3000,
            });

            return true;
        } catch (error) {
            handleError(error, "Failed to remove user from organization");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Make a user a manager
     */
    const makeUserManager = async (userId: string) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response } =
                await api.makeUserManagerUserUserIdMakeUserManagerPost({
                    path: {
                        user_id: userId,
                    },
                });

            if (!response.ok) {
                currentError.value = "Failed to make user a manager";
                return false;
            }

            // Update the user in the local state
            const userIndex = users.value.findIndex(
                (user) => user.identity === userId
            );
            if (userIndex !== -1) {
                users.value[userIndex] = {
                    ...users.value[userIndex],
                    role: types.ClusterRole.USER_MANAGER,
                };
            } else {
                // Refresh the user list if we couldn't find the user locally
                await getAllUsers();
            }

            return true;
        } catch (error) {
            handleError(error, "Failed to make user a manager");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Make a user a regular user (not manager)
     */
    const makeUser = async (userId: string) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response } = await api.makeUserUserUserIdMakeUserPost({
                path: {
                    user_id: userId,
                },
            });

            if (!response.ok) {
                currentError.value = "Failed to update user role";
                return false;
            }

            // Update the user in the local state
            const userIndex = users.value.findIndex(
                (user) => user.identity === userId
            );
            if (userIndex !== -1) {
                users.value[userIndex] = {
                    ...users.value[userIndex],
                    role: types.ClusterRole.USER,
                };
            } else {
                // Refresh the user list if we couldn't find the user locally
                await getAllUsers();
            }

            return true;
        } catch (error) {
            handleError(error, "Failed to update user role");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Update a user's authorization level in the organization
     */
    const updateUserAuthorizationLevel = async (
        userId: string,
        role: types.ClusterRole
    ) => {
        isLoading.value = true;
        currentError.value = null;

        let success = false;

        try {
            if (role === types.ClusterRole.USER_MANAGER) {
                success = await makeUserManager(userId);
            } else {
                success = await makeUser(userId);
            }

            if (success) {
                toast.add({
                    severity: "success",
                    summary: "User role updated successfully",
                    life: 3000,
                });
            }

            return success;
        } catch (error) {
            handleError(error, "Failed to update user role");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Helper function to handle API errors
     */
    const handleError = (error: unknown, defaultMessage: string) => {
        if (isHttpExceptionModel(error)) {
            currentError.value = error.detail[0]?.message || defaultMessage;
            toast.add({
                severity: "error",
                summary: error.detail[0]?.message || defaultMessage,
                life: 5000,
            });
        } else {
            currentError.value = defaultMessage;
            toast.add({
                severity: "error",
                summary: defaultMessage,
                life: 5000,
            });
        }
        console.error(error);
    };

    return {
        users,
        userRoles,
        isLoading,
        currentError,
        getAllUsers,
        getUserRoles,
        addUserToOrganization,
        removeUserFromOrganization,
        updateUserAuthorizationLevel,
        hasUsers: computed(() => users.value.length > 0),
    };
});

export const useUsersManager = () => storeToRefs(useUsersStore());
